(() => {
  const TRANSITION_MS = 280;
  const hideTimers = new WeakMap();

  const initChoose = (root) => {
    if (root.dataset.nanoChooseReady === 'true') return;
    root.dataset.nanoChooseReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-nano-choose-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-nano-choose-panel]'));
    const details = Array.from(root.querySelectorAll('[data-nano-choose-detail]'));
    const tabScroll = root.querySelector('[data-nano-choose-tab-scroll]');
    const tabViewport = root.querySelector('[data-nano-choose-tab-viewport]');
    const tabList = root.querySelector('[data-nano-choose-tabs]');
    const prevBtn = root.querySelector('[data-nano-choose-tab-prev]');
    const nextBtn = root.querySelector('[data-nano-choose-tab-next]');

    if (tabs.length === 0) return;

    let minX = 0;
    let currentX = 0;

    const clampX = (x) => Math.max(minX, Math.min(0, x));

    const normalizeWheelDelta = (event) => {
      if (!tabViewport) return event.deltaX;
      if (event.deltaMode === 1) return event.deltaX * 16;
      if (event.deltaMode === 2) return event.deltaX * tabViewport.clientWidth;
      return event.deltaX;
    };

    const getViewportContentWidth = () => {
      if (!tabViewport) return 0;

      const viewportStyle = window.getComputedStyle(tabViewport);
      const viewportPadding =
        Number.parseFloat(viewportStyle.paddingLeft) +
        Number.parseFloat(viewportStyle.paddingRight);
      return tabViewport.clientWidth - viewportPadding;
    };

    const updateTabOverflow = () => {
      if (!tabScroll || !tabViewport || !tabList) return;

      const hasOverflow = minX < 0;

      tabScroll.classList.toggle('is-overflowing', hasOverflow);
      tabScroll.classList.toggle('has-overflow-left', hasOverflow && currentX < -1);
      tabScroll.classList.toggle('has-overflow-right', hasOverflow && currentX > minX + 1);
    };

    const setTabX = (x, { animate = false } = {}) => {
      if (!tabList) return;

      currentX = clampX(x);
      tabList.style.transition = animate ? 'transform 320ms ease' : 'none';
      tabList.style.transform = `translate3d(${currentX}px, 0, 0)`;
      updateTabOverflow();
    };

    const refreshTabDrag = () => {
      if (!tabViewport || !tabList) return;

      const overflow = tabList.scrollWidth - getViewportContentWidth();
      minX = overflow > 1 ? -overflow : 0;
      setTabX(minX === 0 ? 0 : currentX);
    };

    const centerTab = (tab, { animate = true } = {}) => {
      if (!tab || !tabViewport) return;

      const targetX = -(tab.offsetLeft + tab.offsetWidth / 2 - getViewportContentWidth() / 2);
      setTabX(targetX, { animate });
    };

    const dragState = {
      pointerId: null,
      startX: 0,
      startTranslate: 0,
      targetTab: null,
      dragging: false,
      suppressClick: false,
    };

    const reveal = (items, index) => {
      items.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        const hideTimer = hideTimers.get(item);

        if (hideTimer) {
          window.clearTimeout(hideTimer);
          hideTimers.delete(item);
        }

        if (active) {
          item.hidden = false;
          window.requestAnimationFrame(() => item.classList.add('is-active'));
          return;
        }

        item.classList.remove('is-active');
        hideTimers.set(item, window.setTimeout(() => {
          if (!item.classList.contains('is-active')) item.hidden = true;
          hideTimers.delete(item);
        }, TRANSITION_MS));
      });
    };

    const setActive = (index, shouldScroll = true) => {
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      reveal(panels, index);
      reveal(details, index);

      if (shouldScroll) {
        centerTab(tabs[index], { animate: true });
        window.setTimeout(refreshTabDrag, 320);
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        if (dragState.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const index = Number.parseInt(tab.dataset.nanoChooseTab || '0', 10);
        setActive(index);
      });
    });

    const clearSuppressClick = () => {
      dragState.suppressClick = false;
    };

    tabViewport?.addEventListener('pointerdown', (event) => {
      if (minX === 0) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      dragState.pointerId = event.pointerId;
      dragState.startX = event.clientX;
      dragState.startTranslate = currentX;
      dragState.targetTab = event.target.closest('[data-nano-choose-tab]');
      dragState.dragging = false;
      dragState.suppressClick = false;
      tabList.style.transition = 'none';
      tabViewport.setPointerCapture?.(event.pointerId);
    });

    tabViewport?.addEventListener('pointermove', (event) => {
      if (dragState.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - dragState.startX;
      if (!dragState.dragging && Math.abs(deltaX) > 6) {
        dragState.dragging = true;
        dragState.suppressClick = true;
      }

      if (!dragState.dragging) return;

      setTabX(dragState.startTranslate + deltaX);
      event.preventDefault();
    });

    const endDrag = (event) => {
      if (dragState.pointerId !== event.pointerId) return;

      if (tabViewport?.hasPointerCapture?.(event.pointerId)) {
        tabViewport.releasePointerCapture(event.pointerId);
      }

      dragState.pointerId = null;
      if (dragState.dragging) {
        setTabX(currentX);
        window.setTimeout(clearSuppressClick, 80);
      } else if (dragState.targetTab) {
        const index = Number.parseInt(dragState.targetTab.dataset.nanoChooseTab || '0', 10);
        if (Number.isFinite(index)) {
          setActive(index);
        }
        dragState.suppressClick = true;
        window.setTimeout(clearSuppressClick, 80);
      } else {
        dragState.suppressClick = false;
      }
      dragState.targetTab = null;
      dragState.dragging = false;
    };

    tabViewport?.addEventListener('pointerup', endDrag);
    tabViewport?.addEventListener('pointercancel', endDrag);
    tabViewport?.addEventListener('lostpointercapture', () => {
      dragState.pointerId = null;
      dragState.targetTab = null;
      dragState.dragging = false;
      window.setTimeout(clearSuppressClick, 80);
    });

    prevBtn?.addEventListener('click', () => {
      if (!tabViewport) return;
      setTabX(currentX + tabViewport.clientWidth * 0.65, { animate: true });
      window.setTimeout(refreshTabDrag, 320);
    });

    nextBtn?.addEventListener('click', () => {
      if (!tabViewport) return;
      setTabX(currentX - tabViewport.clientWidth * 0.65, { animate: true });
      window.setTimeout(refreshTabDrag, 320);
    });

    tabViewport?.addEventListener(
      'wheel',
      (event) => {
        if (minX === 0) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        setTabX(currentX - deltaX);
      },
      { passive: false }
    );

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(refreshTabDrag, 120);
    });

    const parsedDefaultIndex = Number.parseInt(root.dataset.defaultActive || '0', 10);
    const defaultIndex = Number.isFinite(parsedDefaultIndex)
      ? Math.max(0, Math.min(tabs.length - 1, parsedDefaultIndex))
      : 0;
    setActive(defaultIndex, false);
    refreshTabDrag();
    centerTab(tabs[defaultIndex], { animate: false });
    requestAnimationFrame(refreshTabDrag);
    window.setTimeout(refreshTabDrag, 80);
    document.fonts?.ready?.then(refreshTabDrag).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-nano-choose]').forEach(initChoose);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
